import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import pb from '../../../lib/pocketbase';

const ServiceCatalog = () => {
    const [services, setServices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingService, setEditingService] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        category: 'Clés',
        sub_category: 'Voiture',
        base_price: 0,
        price_citadine: 0,
        price_berline: 0,
        price_suv: 0,
        price_luxe: 0,
        price_utilitaire: 0,
        surcharge_recent_year: 0,
        recent_year_threshold: 2018,
        duration: '',
        published: true
    });

    const categories = ['Clés', 'Éthanol', 'Diagnostic', 'Moteur'];
    const subCategories = ['Voiture', 'Moto', 'Scooter', 'Poids Lourd', 'Autre'];

    const fetchServices = async () => {
        setLoading(true);
        try {
            const records = await pb.collection('services').getFullList({
                sort: 'category,sub_category,name',
            });
            setServices(records);
        } catch (err) {
            console.error('Error fetching services:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchServices();
    }, []);

    const handleOpenModal = (service = null) => {
        if (service) {
            setEditingService(service);
            setFormData({
                name: service.name,
                category: service.category,
                sub_category: service.sub_category || 'Voiture',
                base_price: service.base_price,
                price_citadine: service.price_citadine || service.base_price,
                price_berline: service.price_berline || service.base_price,
                price_suv: service.price_suv || service.base_price,
                price_luxe: service.price_luxe || service.base_price,
                price_utilitaire: service.price_utilitaire || service.base_price,
                surcharge_recent_year: service.surcharge_recent_year || 0,
                recent_year_threshold: service.recent_year_threshold || 2018,
                duration: service.duration,
                published: service.published !== false
            });
        } else {
            setEditingService(null);
            setFormData({
                name: '',
                category: 'Clés',
                sub_category: 'Voiture',
                base_price: 0,
                price_citadine: 0,
                price_berline: 0,
                price_suv: 0,
                price_luxe: 0,
                price_utilitaire: 0,
                surcharge_recent_year: 0,
                recent_year_threshold: 2018,
                duration: '',
                published: true
            });
        }
        setShowModal(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            if (editingService) {
                await pb.collection('services').update(editingService.id, formData);
            } else {
                await pb.collection('services').create(formData);
            }
            setShowModal(false);
            fetchServices();
        } catch (err) {
            alert('Erreur lors de l\'enregistrement : ' + err.message);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Supprimer ce service ?')) {
            try {
                await pb.collection('services').delete(id);
                fetchServices();
            } catch (err) {
                alert('Erreur lors de la suppression');
            }
        }
    };

    if (loading) return <div className="admin-content-wrapper">Chargement du catalogue...</div>;

    return (
        <>
            <div className="admin-content-wrapper">
                <section className="intro-card admin-intro">
                    <div className="admin-header-content">
                        <h2>Catalogue des Services</h2>
                        <p>Gérez vos prestations, tarifs par catégorie/sous-catégorie et majorations années.</p>
                    </div>
                    <div className="admin-header-actions">
                        <button className="primary-button" onClick={() => handleOpenModal()}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>
                            Nouveau Service
                        </button>
                    </div>
                </section>

                <section className="card full-width-card">
                    <div className="table-responsive">
                        <table className="data-table admin-table">
                            <thead>
                                <tr>
                                    <th>Prestation</th>
                                    <th>Cat / Sous-Cat</th>
                                    <th>Tarif Base</th>
                                    <th>Citadine/SUV</th>
                                    <th>Montant Luxe</th>
                                    <th>Durée</th>
                                    <th>Statut</th>
                                    <th className="text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {services.map((service) => (
                                    <tr key={service.id}>
                                        <td><strong>{service.name}</strong></td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '5px' }}>
                                                <span className={`service-tag ${service.category.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")}`}>{service.category}</span>
                                                <span className="id-cell" style={{ fontSize: '0.7rem' }}>{service.sub_category}</span>
                                            </div>
                                        </td>
                                        <td>{service.base_price} €</td>
                                        <td>{service.price_citadine} / {service.price_suv} €</td>
                                        <td>{service.price_luxe} €</td>
                                        <td style={{ fontSize: '0.8rem' }}>{service.duration || '-'}</td>
                                        <td>
                                            <span
                                                className={`status-badge ${service.published !== false ? 'completed' : 'pending'}`}
                                                style={{ cursor: 'pointer', userSelect: 'none' }}
                                                onClick={async () => {
                                                    try {
                                                        await pb.collection('services').update(service.id, { published: !service.published });
                                                        fetchServices();
                                                    } catch (err) {
                                                        alert('Erreur lors du changement de statut');
                                                    }
                                                }}
                                            >
                                                {service.published !== false ? 'En ligne' : 'Brouillon'}
                                            </span>
                                        </td>
                                        <td className="text-right">
                                            <div className="action-buttons">
                                                <button
                                                    className="icon-btn"
                                                    title={service.published !== false ? "Dépublier" : "Publier"}
                                                    onClick={async () => {
                                                        try {
                                                            await pb.collection('services').update(service.id, { published: !service.published });
                                                            fetchServices();
                                                        } catch (err) {
                                                            alert('Erreur lors du changement de statut');
                                                        }
                                                    }}
                                                    style={{ color: service.published !== false ? '#4ade80' : '#94a3b8' }}
                                                >
                                                    {service.published !== false ? (
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>
                                                    ) : (
                                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                                                    )}
                                                </button>
                                                <button className="icon-btn" title="Modifier" onClick={() => handleOpenModal(service)}>
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                                                </button>
                                                <button className="icon-btn" title="Supprimer" onClick={() => handleDelete(service.id)}>
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>

            {showModal && createPortal(
                <div className="modal-backdrop" onClick={() => setShowModal(false)} style={{ zIndex: 99999 }}>
                    <div className="modal-content admin-modal-large" onClick={e => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <h2>{editingService ? 'Modifier la prestation' : 'Nouvelle prestation'}</h2>
                            <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
                        </div>

                        <form onSubmit={handleSave}>
                            <div className="admin-modal-body">
                                <div className="pricing-input-grid">
                                    <section>
                                        <div className="admin-modal-section-title">Informations Générales</div>

                                        <div className="form-group" style={{ marginBottom: '1.25rem' }}>
                                            <label>Nom de la prestation</label>
                                            <input
                                                type="text"
                                                className="admin-input"
                                                value={formData.name}
                                                onChange={e => setFormData({ ...formData, name: e.target.value })}
                                                placeholder="Ex: Reproduction de clé complète"
                                                required
                                            />
                                        </div>

                                        <div className="grid-2" style={{ marginBottom: '1.25rem' }}>
                                            <div className="form-group">
                                                <label>Catégorie</label>
                                                <select
                                                    className="admin-input"
                                                    value={formData.category}
                                                    onChange={e => setFormData({ ...formData, category: e.target.value })}
                                                >
                                                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label>Sous-catégorie</label>
                                                <select
                                                    className="admin-input"
                                                    value={formData.sub_category}
                                                    onChange={e => setFormData({ ...formData, sub_category: e.target.value })}
                                                >
                                                    {subCategories.map(sc => <option key={sc} value={sc}>{sc}</option>)}
                                                </select>
                                            </div>
                                        </div>

                                        <div className="form-group" style={{ marginBottom: '1.25rem' }}>
                                            <label>Durée estimée</label>
                                            <input
                                                type="text"
                                                className="admin-input"
                                                value={formData.duration}
                                                onChange={e => setFormData({ ...formData, duration: e.target.value })}
                                                placeholder="ex: 1h30"
                                            />
                                        </div>

                                        <div className="form-group" style={{ marginTop: '0.5rem' }}>
                                            <label className="checkbox-item active" style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.5rem 0', border: 'none', background: 'transparent' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={formData.published}
                                                    onChange={e => setFormData({ ...formData, published: e.target.checked })}
                                                />
                                                <span style={{ fontSize: '0.9rem', fontWeight: 600 }}>Prestation active (visible sur le site)</span>
                                            </label>
                                        </div>

                                        <div className="surcharge-card">
                                            <div style={{ fontSize: '0.8rem', fontWeight: 700, color: '#facc15', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                <span>📅</span> Majoration Véhicule Récent
                                            </div>
                                            <div className="grid-2">
                                                <div className="form-group">
                                                    <label>Seuil année (≥)</label>
                                                    <input type="number" className="admin-input" value={formData.recent_year_threshold} onChange={e => setFormData({ ...formData, recent_year_threshold: Number(e.target.value) })} />
                                                </div>
                                                <div className="form-group">
                                                    <label>Surcoût (€)</label>
                                                    <input type="number" className="admin-input" value={formData.surcharge_recent_year} onChange={e => setFormData({ ...formData, surcharge_recent_year: Number(e.target.value) })} />
                                                </div>
                                            </div>
                                        </div>
                                    </section>

                                    <section>
                                        <div className="admin-modal-section-title">Grille Tarifaire (€)</div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                            <div className="pricing-field">
                                                <label>🏠 Prix de Base</label>
                                                <input type="number" value={formData.base_price} onChange={e => setFormData({ ...formData, base_price: Number(e.target.value) })} />
                                            </div>
                                            <div className="pricing-field">
                                                <label>🚗 Citadine</label>
                                                <input type="number" value={formData.price_citadine} onChange={e => setFormData({ ...formData, price_citadine: Number(e.target.value) })} />
                                            </div>
                                            <div className="pricing-field">
                                                <label>🚘 Berline</label>
                                                <input type="number" value={formData.price_berline} onChange={e => setFormData({ ...formData, price_berline: Number(e.target.value) })} />
                                            </div>
                                            <div className="pricing-field">
                                                <label>🚙 SUV / 4x4</label>
                                                <input type="number" value={formData.price_suv} onChange={e => setFormData({ ...formData, price_suv: Number(e.target.value) })} />
                                            </div>
                                            <div className="pricing-field" style={{ borderColor: 'rgba(250, 204, 21, 0.3)' }}>
                                                <label>🏎️ Luxe / Sport</label>
                                                <input type="number" value={formData.price_luxe} onChange={e => setFormData({ ...formData, price_luxe: Number(e.target.value) })} />
                                            </div>
                                            <div className="pricing-field">
                                                <label>🚐 Utilitaire</label>
                                                <input type="number" value={formData.price_utilitaire} onChange={e => setFormData({ ...formData, price_utilitaire: Number(e.target.value) })} />
                                            </div>
                                        </div>

                                        <p style={{ fontSize: '0.7rem', color: '#64748b', marginTop: '1.5rem', fontStyle: 'italic' }}>
                                            * Les tarifs personnalisés seront appliqués automatiquement lors de la simulation de devis client.
                                        </p>
                                    </section>
                                </div>
                            </div>

                            <div className="admin-modal-footer">
                                <button type="button" className="back-to-site-btn" onClick={() => setShowModal(false)} style={{ background: 'transparent' }}>Annuler</button>
                                <button type="submit" className="primary-button" style={{ width: 'auto', margin: 0, padding: '0.75rem 2.5rem' }}>
                                    {editingService ? 'Mettre à jour la prestation' : 'Créer la prestation'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

export default ServiceCatalog;
